export const buildTreeData = (nodes) => {
  const roots = nodes.filter((n) => n.parentId === null);

  const result = [];

  const traverse = (node, path = []) => {
    const fullPath = [...path, node];
    result.push({ ...node, _path: fullPath });
    const children = nodes.filter((n) => n.parentId === node.energyTreeNodeId);
    children.forEach((child) => traverse(child, fullPath));
  };

  roots.forEach((root) => traverse(root));
  return result;
};

const getAncestors = (nodeId, tree) => {
  if (!nodeId || !tree || tree.length === 0) return [];
  const map = new Map(tree.map((n) => [n.energyTreeNodeId, n]));
  const ancestors = [];
  let current = map.get(nodeId);
  while (current?.parentId != null) {
    const parent = map.get(current.parentId);
    if (!parent) break;
    ancestors.push(parent);
    current = parent;
  }
  return ancestors;
};

const INFRA = new Set(['Hovedmåler', 'Tavle', 'Undertavle']); // Infrastructure types
const isInfraNode = (t) => INFRA.has(t);
const isMainMeter = (t) => t === 'Hovedmåler';
const isDistributionBoard = (t) => t === 'Tavle';
const isSubBoard = (t) => t === 'Undertavle';
const isSEUNode = (t) => !!t && !INFRA.has(t); // Representing a Significant Energy User (SEU)
const isDataNode = (n) => n?.dataStreams?.length > 0;
const isVirtualNode = (n) => !isDataNode(n);
export const isResidualNode = (n) => !isDataNode(n) && n?.options !== null;

/**
 * Validates whether a node of a specific type can be added to a given parent node in the tree.
 *
 * @param {string} params.targetType - The type of the node to be added (e.g., 'Hovedmåler', 'Tavle', 'Undertavle').
 * @param {Object|null} params.parent - The parent node under which the new node is to be added. Can be null for root nodes.
 * @param {Array<Object>} params.tree - The entire tree structure represented as an array of nodes.
 *
 * @returns {Object} - An object containing the validation result.
 * @property {boolean} isValid - Indicates whether the addition is valid.
 * @property {string} [invalidReason] - The reason why the addition is invalid, if applicable.
 *
 * @example
 * const tree = [
 *   { energyTreeNodeId: 1, type: 'Hovedmåler', parentId: null },
 *   { energyTreeNodeId: 2, type: 'Tavle', parentId: 1 },
 * ];
 * const parent = tree[1];
 * const result = validateAddNode({ targetType: 'Undertavle', parent, tree });
 * console.log(result); // { isValid: true }
 */
export const validateAddNode = ({ targetType, parent, tree }) => {
  if (parent && isSEUNode(parent.type) && isDataNode(parent)) {
    return {
      isValid: false,
      invalidReason: 'Kan ikke tilføje node under en SEU node med data',
    };
  }

  if (parent && isResidualNode(parent)) {
    return {
      isValid: false,
      invalidReason: 'Kan ikke tilføje node under en residual node',
    };
  }
  if (targetType === 'Hovedmåler') {
    const mainMeterExists = tree.some((n) => n.type === 'Hovedmåler');
    if (mainMeterExists) {
      return {
        isValid: false,
        invalidReason: 'Hovedmåler eksisterer allerede.',
      };
    }
    if (parent && parent.type !== null) {
      return {
        isValid: false,
        invalidReason: 'Hovedmåler skal være roden (ingen parent).',
      };
    }
    return { isValid: true };
  }

  // Tavle only under Hovedmåler or Virtual (no data)
  if (targetType === 'Tavle') {
    const parentIsMain = parent?.type === 'Hovedmåler';
    const parentIsVirtual = !!parent && !parent.dataStreams?.length; // your isVirtualNode(parent)
    if (!parentIsMain && !parentIsVirtual) {
      return {
        isValid: false,
        invalidReason:
          'Tavle må kun placeres under Hovedmåler eller node uden data.',
      };
    }
    return { isValid: true };
  }

  // Undertavle only under Tavle (or under a branch with Tavle)
  if (targetType === 'Undertavle') {
    const ancestors = parent?.energyTreeNodeId
      ? getAncestors(parent.energyTreeNodeId, tree)
      : [];

    const hasTavleInLineage =
      parent?.type === 'Tavle' || ancestors.some((a) => a.type === 'Tavle');

    const hasUndertavleInLineage =
      parent?.type === 'Undertavle' ||
      ancestors.some((a) => a.type === 'Undertavle');

    if (hasUndertavleInLineage) {
      return {
        isValid: false,
        invalidReason:
          'En Undertavle må ikke placeres under en gren som allerede indeholder en Undertavle.',
      };
    }

    if (!hasTavleInLineage) {
      return {
        isValid: false,
        invalidReason:
          'Undertavle må kun placeres under (eller under en gren med) en Tavle.',
      };
    }

    return { isValid: true };
  }

  // default allow
  return { isValid: true };
};

/**
 * Validates whether a node can be edited to a specific type in the energy tree structure.
 *
 * @param {Object} params.currentNode - The current node being edited.
 * @param {string} params.targetType - The type to which the node is being changed (e.g., 'Hovedmåler', 'Tavle', 'Undertavle').
 * @param {Array<Object>} params.tree - The entire tree structure represented as an array of nodes.
 * @param {Array<Object>} [params.dataStreams] - The data streams associated with the current node, if any.
 *
 * @returns {Object} - An object containing the validation result.
 * @property {boolean} isValid - Indicates whether the edit is valid.
 * @property {boolean} [isUnitSelectionDisabled] - Indicates whether unit selection should be disabled in the UI.
 * @property {string} [unitSelectionDisabledReason] - The reason why unit selection is disabled, if applicable.
 * @property {string} [invalidReason] - The reason why the edit is invalid, if applicable.
 *
 * @example
 * const tree = [
 *   { energyTreeNodeId: 1, type: 'Hovedmåler', parentId: null },
 *   { energyTreeNodeId: 2, type: 'Tavle', parentId: 1 },
 * ];
 * const currentNode = tree[1];
 * const result = validateEditNode({
 *   currentNode,
 *   targetType: 'Undertavle',
 *   tree,
 *   dataStreams: [],
 * });
 * console.log(result); // { isValid: false, invalidReason: 'En Tavle kan ikke ændres til Hovedmåler eller Undertavle.' }
 */

export const validateEditNode = ({
  currentNode,
  targetType,
  tree,
  dataStreams,
}) => {
  if (!currentNode) {
    return { isValid: false, invalidReason: 'Mangler den nuværende node.' };
  }

  const byId = new Map(tree.map((n) => [n.energyTreeNodeId, n]));
  const parent =
    currentNode.parentId != null ? byId.get(currentNode.parentId) : null;

  if (
    isSEUNode(currentNode.type) &&
    isDataNode(currentNode) &&
    isInfraNode(targetType)
  ) {
    return {
      isValid: false,
      invalidReason:
        'SEU med data kan kun ændres til en anden SEU-type (ikke infrastruktur).',
    };
  }

  if (isDistributionBoard(currentNode.type)) {
    const hasUndertavleChildren = tree.some(
      (n) =>
        n.parentId === currentNode.energyTreeNodeId && n.type === 'Undertavle',
    );
    if (hasUndertavleChildren && targetType !== 'Tavle') {
      return {
        isValid: false,
        invalidReason:
          'En Tavle med undertavle-børn kan kun ændres til en anden Tavle.',
      };
    }
  }

  if (isSubBoard(currentNode.type)) {
    const hasChildren = tree.some(
      (n) => n.parentId === currentNode.energyTreeNodeId,
    );
    if (hasChildren && targetType !== 'Undertavle') {
      return {
        isValid: false,
        invalidReason:
          'En Undertavle med børn kan kun ændres til en anden Undertavle.',
      };
    }
  }

  if (isVirtualNode(currentNode)) {
    const childNodes = tree.filter(
      (n) => n.parentId === currentNode.energyTreeNodeId,
    );
    const hasChildSEUWithData = childNodes.some(
      (c) => isSEUNode(c.type) && c.dataStreams?.length > 0,
    );

    if (hasChildSEUWithData) {
      return {
        isValid: true,
        isUnitSelectionDisabled: true, // <-- use this to disable the control in the UI
        unitSelectionDisabledReason:
          'Node har børn med data, så datakilder kan ikke ændres.',
      };
    }
  }

  if (isDistributionBoard(currentNode.type) && isSEUNode(targetType)) {
    if (dataStreams?.length > 0) {
      return {
        isValid: false,
        invalidReason:
          'Tavle med data kan kun ændres til en anden Tavle (ikke SEU). Du kan rydde data først, hvis du vil ændre type til en SEU.',
      };
    } else if (dataStreams?.length === 0) {
      return { isValid: true };
    }
  }

  if (isSubBoard(currentNode.type) && isSEUNode(targetType)) {
    if (dataStreams?.length > 0) {
      return {
        isValid: false,
        invalidReason:
          'Undertavle med data kan kun ændres til en anden Undertavle (ikke SEU). Du kan rydde data først, hvis du vil ændre type til en SEU.',
      };
    } else if (dataStreams?.length === 0) {
      return { isValid: true };
    }
  }

  if (isVirtualNode(currentNode) && isInfraNode(targetType)) {
    return {
      isValid: false,
      invalidReason:
        'Virtuel node (uden data) kan kun ændres til en SEU-type (ikke infrastruktur).',
    };
  }

  if (
    currentNode.type === 'Tavle' &&
    (targetType === 'Hovedmåler' || targetType === 'Undertavle')
  ) {
    return {
      isValid: false,
      invalidReason:
        'En Tavle kan ikke ændres til Hovedmåler eller Undertavle.',
    };
  }

  if (targetType === 'Hovedmåler') {
    const otherMainExists = tree.some(
      (n) =>
        n.type === 'Hovedmåler' &&
        n.energyTreeNodeId !== currentNode.energyTreeNodeId,
    );
    if (otherMainExists) {
      return {
        isValid: false,
        invalidReason: 'Hovedmåler eksisterer allerede.',
      };
    }
    if (currentNode.parentId !== null) {
      return {
        isValid: false,
        invalidReason: 'Hovedmåler skal være roden (ingen parent).',
      };
    }
    return { isValid: true };
  }

  if (targetType === 'Tavle') {
    const parentIsMain = parent?.type === 'Hovedmåler';
    const parentIsVirtual = !!parent && !isDataNode(parent);
    if (!parentIsMain && !parentIsVirtual) {
      return {
        isValid: false,
        invalidReason:
          'Tavle må kun placeres under Hovedmåler eller node uden data.',
      };
    }
    return { isValid: true };
  }

  if (targetType === 'Undertavle') {
    const ancestors = parent?.energyTreeNodeId
      ? getAncestors(parent.energyTreeNodeId, tree)
      : [];
    const hasTavleInLineage =
      parent?.type === 'Tavle' || ancestors.some((a) => a.type === 'Tavle');
    const hasUndertavleInLineage =
      parent?.type === 'Undertavle' ||
      ancestors.some((a) => a.type === 'Undertavle');

    if (hasUndertavleInLineage) {
      return {
        isValid: false,
        invalidReason:
          'En Undertavle må ikke placeres under en gren som allerede indeholder en Undertavle.',
      };
    }
    if (!hasTavleInLineage) {
      return {
        isValid: false,
        invalidReason:
          'Undertavle må kun placeres under (eller under en gren med) en Tavle.',
      };
    }
    return { isValid: true };
  }

  // Default allow
  return { isValid: true };
};

export const filterOptionGroupsForAdd = (parent, tree, groupedOptions) => {
  return Object.fromEntries(
    Object.entries(groupedOptions).map(([groupLabel, options]) => {
      const filtered = options.filter(({ value }) => {
        const { isValid } = validateAddNode({
          targetType: value,
          parent,
          tree,
        });
        return isValid;
      });
      return [groupLabel, filtered];
    }),
  );
};

export const filterOptionGroupsForEdit = (
  parent,
  tree,
  groupedOptions,
  dataStreams,
) => {
  return Object.fromEntries(
    Object.entries(groupedOptions).map(([groupLabel, options]) => {
      const filtered = options.filter(({ value }) => {
        const { isValid } = validateEditNode({
          targetType: value,
          parent,
          currentNode: parent,
          tree,
          dataStreams: dataStreams,
        });
        return isValid;
      });
      return [groupLabel, filtered];
    }),
  );
};

export const actionValidation = ({
  action,
  targetType,
  parent,
  currentNode,
  dataStreams,
  tree,
  options,
  isMainMeterUsed,
}) => {
  switch (action) {
    case 'add':
      return validateAddNode({ targetType, parent, tree, options });
    case 'edit':
      return validateEditNode({ currentNode, targetType, tree, dataStreams });
    case 'move':
      return validateMoveNode({
        action,
        targetType,
        parentType: parent.type,
        currentNode: currentNode,
        parent: parent,
        parentUnits: parent.dataStreams?.length || 0,
        isMainMeterUsed: isMainMeterUsed,
      });
    default:
      return { isValid: false, invalidReason: 'Unknown action' };
  }
};

export const validateMoveNode = ({
  targetType,
  parentType,
  parent,
  currentNode,
  isMainMeterUsed,
  tree,
}) => {
  if (isMainMeter(targetType) && isMainMeterUsed) {
    return { isValid: false, invalidReason: 'Hovedmåler already exists' };
  }

  if (isResidualNode(parent)) {
    return {
      isValid: false,
      invalidReason:
        'Det er ikke muligt at flytte en node under en residual node.',
    };
  }

  if (
    parent &&
    isInfraNode(targetType) &&
    isDataNode(parent) &&
    isSEUNode(parent.type)
  ) {
    return {
      isValid: false,
      invalidReason: `Det er ikke muligt at flytte en ${targetType} under en ${parent.type} node.`,
    };
  }

  if (
    parent &&
    isSubBoard(targetType) &&
    !getAncestors(parent.energyTreeNodeId, tree).some(
      (a) => a.type === 'Tavle',
    ) &&
    parent.type !== 'Tavle'
  ) {
    return {
      isValid: false,
      invalidReason: `Det er ikke muligt at flytte en ${targetType} under en ${parent.type} node.`,
    };
  }

  // cant move a residual node under a SEU node with data
  if (parent && isSEUNode(parentType) && isResidualNode(currentNode)) {
    return {
      isValid: false,
      invalidReason: `Det er ikke muligt at flytte en ${isResidualNode(currentNode) ? 'Residual Node' : ''} under en ${parent.type} node.`,
    };
  }

  // cant move a node under a SEU node with data
  if (parent && isSEUNode(parentType) && isDataNode(parent)) {
    return {
      isValid: false,
      invalidReason: `Det er ikke muligt at flytte en ${targetType} under en ${parent.type} node.`,
    };
  }

  // if (
  //   targetType === 'Tavle' &&
  //   parentType !== 'Hovedmåler' &&
  //   parentUnits > 0
  // ) {
  //   return {
  //     valid: false,
  //     reason: 'Tavle må kun være under Hovedmåler eller node uden units',
  //   };
  // }

  // if (!isInfraNode(targetType) && parentUnits > 0) {
  //   return {
  //     isValid: false,
  //     invalidReason:
  //       'Kan ikke flyttes under node med data som ikke er en Tavle, Undertavle eller Hovedmåler',
  //   };
  // }

  return { isValid: true };
};

export const getParent = (nodeId, treeData) => {
  const map = new Map(treeData.map((n) => [n.energyTreeNodeId, n]));
  const current = map.get(nodeId);
  return map.get(current?.parentId) || null;
};

export const getChildren = (nodeId, treeData) => {
  return treeData.filter((n) => n.parentId === nodeId);
};

export const isNodeDescendant = (childId, parentId, treeData) => {
  const childNode = treeData.find((node) => node.energyTreeNodeId === childId);
  if (!childNode || !childNode.parentId) return false;
  if (childNode.parentId === parentId) return true;
  return isNodeDescendant(childNode.parentId, parentId, treeData);
};

export const getUnitQueryOptions = (unitTypeId) => {
  const switchCase = {
    61: { unitTypeInputId: 6101, dataType: 'power-consumption' },
    28: { unitTypeInputId: 2801, dataType: 'apparent-power' },
    30: { unitTypeInputId: 0, dataType: 'power' },
  };

  // Return the mapped value if it exists, otherwise return null
  return switchCase[unitTypeId] || null;
};

export const mapUnitsToDataStreams = (units) => {
  return units
    .map((unit) => {
      const queryOptions = getUnitQueryOptions(unit.UnitType?.UnitTypeId);
      if (!queryOptions) return null;
      return {
        unitId: unit.UnitId,
        unitTypeInputId: queryOptions.unitTypeInputId ?? null, // Safe access
        dataType: queryOptions.dataType || 'Power', // Defaulting to "Power"
        aggregateType: 'Hour', // Defaulting to "Hour"
      };
    })
    .filter(Boolean);
};

export const canAddNode = (clickedNode) => {
  if (!clickedNode) return false;
  console.table({
    type: clickedNode.type,
    isInfra: isInfraNode(clickedNode.type),
    isSEU: isSEUNode(clickedNode.type),
    isDataNode: isDataNode(clickedNode),
    isVirtualNode: isVirtualNode(clickedNode),
    isResidualNode: isResidualNode(clickedNode),
  });
};

// Build a fast lookup
const mapById = (tree) => new Map(tree.map((n) => [n.energyTreeNodeId, n]));

const computeNodeViolations = (node, tree, counts) => {
  const issues = [];
  const parent =
    node.parentId != null ? mapById(tree).get(node.parentId) : null;

  // Rule: only one Hovedmåler in the entire tree, and it must be root (no parent)
  if (node.type === 'Hovedmåler') {
    if (counts.hovedmålerCount > 1) {
      issues.push({
        code: 'MULTIPLE_MAIN_METERS',
        message: 'Der må kun være én Hovedmåler i træet.',
      });
    }
    if (node.parentId !== null) {
      issues.push({
        code: 'MAIN_METER_NOT_ROOT',
        message: 'Hovedmåler skal være roden (ingen parent).',
      });
    }
  }

  // Rule: Tavle only directly under Hovedmåler or a Virtual node
  if (node.type === 'Tavle') {
    const parentIsMain = parent?.type === 'Hovedmåler';
    const parentIsVirtual = !!parent && isVirtualNode(parent);
    if (!parentIsMain && !parentIsVirtual) {
      issues.push({
        code: 'TAVLE_PARENT_INVALID',
        message: 'Tavle må kun placeres under Hovedmåler eller node uden data.',
      });
    }
  }

  // Rule: Undertavle allowed if ANY ancestor is a Tavle (not just direct parent)
  if (node.type === 'Undertavle') {
    const lineageHasTavle =
      parent?.type === 'Tavle' ||
      getAncestors(node.energyTreeNodeId, tree).some((a) => a.type === 'Tavle');

    if (!lineageHasTavle) {
      issues.push({
        code: 'UNDERTAVLE_WITHOUT_TAVLE_ANCESTOR',
        message: 'Undertavle må kun placeres under (en gren med) en Tavle.',
      });
    }
  }

  return issues;
};

export const validateTree = (tree) => {
  // Count Hovedmåler
  const hovedmålerCount = tree.filter((n) => n.type === 'Hovedmåler').length;

  const counts = { hovedmålerCount };
  const byNode = new Map();

  for (const node of tree) {
    const issues = computeNodeViolations(node, tree, counts);
    if (issues.length) {
      byNode.set(node.energyTreeNodeId, issues);
    }
  }

  // Convenience outputs
  const invalidNodeIds = new Set([...byNode.keys()]);
  const flatErrors = [...byNode.entries()].map(([nodeId, issues]) => ({
    nodeId,
    issues,
  }));

  return {
    hasErrors: invalidNodeIds.size > 0,
    invalidNodeIds,
    violationsByNode: byNode, // Map<nodeId, {code,message}[]>
    errors: flatErrors, // Array<{nodeId, issues:[]}>
  };
};
